import React, { useState, useEffect, useCallback } from 'react';
import './GaleriPage.css';

// Image modal component for lightbox functionality
const ImageModal = ({ isOpen, imageData, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <button 
        onClick={onClose} 
        className="absolute top-4 right-4 z-60 text-white hover:text-gray-300 p-2 text-xl"
        aria-label="Kapat"
      >
        ✕
      </button>
      <div 
        className="relative max-w-6xl w-full max-h-[90vh] bg-black rounded-xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <img 
          src={imageData.full} 
          alt={imageData.caption} 
          className="w-full h-full object-contain"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-4">
          <h3 className="text-xl font-semibold">{imageData.caption}</h3>
          <p className="text-gray-300">{imageData.description}</p>
        </div>
      </div>
    </div>
  );
};

// Gallery item component
const GalleryItem = ({ image, onClick, layout }) => {
  const [loaded, setLoaded] = useState(false);
  
  return (
    <div 
      className={`gallery-item ${layout} overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      onClick={() => onClick(image)}
    >
      <div className="relative h-full">
        <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
        <img 
          src={image.thumbnail} 
          alt={image.caption}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          onLoad={() => setLoaded(true)}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
          <h3 className="text-white text-lg font-bold">{image.caption}</h3>
          <p className="text-white/80 text-sm line-clamp-2">{image.description}</p>
        </div>
      </div>
    </div>
  );
};

function GaleriPage() {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [layout, setLayout] = useState('masonry'); // 'masonry', 'grid', or 'fullwidth'

  // Simulate fetching gallery images from an API
  useEffect(() => {
    // This would be replaced with an actual API call
    setTimeout(() => {
      const galleryImages = [
        {
          id: 1,
          thumbnail: "https://images.unsplash.com/photo-1504630083234-14187a9df0f5?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1504630083234-14187a9df0f5?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "cafe",
          caption: "Kahve Köşemiz",
          description: "Misafirlerimizin keyifli bir kahve deneyimi için tasarlanmış rahat köşeler."
        },
        {
          id: 2,
          thumbnail: "https://images.unsplash.com/photo-1442512595331-e89e73853f31?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1442512595331-e89e73853f31?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "coffee",
          caption: "Özel Kahve Çeşitlerimiz",
          description: "Barista ustalarımızın hazırladığı özel kahve çeşitleri."
        },
        {
          id: 3,
          thumbnail: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "cafe",
          caption: "Şık İç Mekanımız",
          description: "Konforlu ve modern tasarıma sahip kafemizin iç mekan görünümü."
        },
        {
          id: 4,
          thumbnail: "https://images.unsplash.com/photo-1507133750040-4a370f5dca5b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1507133750040-4a370f5dca5b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "coffee",
          caption: "Barista Sanatı",
          description: "Baristalarımızın her kahveye kattığı sanatsal dokunuşlar."
        },
        {
          id: 5,
          thumbnail: "https://images.unsplash.com/photo-1598811629267-fafee3e1c6fc?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1598811629267-fafee3e1c6fc?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "pastries",
          caption: "Taze Pastalarımız",
          description: "Her gün kendi mutfağımızda taze olarak hazırladığımız pastalar."
        },
        {
          id: 6,
          thumbnail: "https://images.unsplash.com/photo-1600093463592-8e36ae95ef56?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1600093463592-8e36ae95ef56?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "breakfast",
          caption: "Kahvaltı Menümüz",
          description: "Hafta sonları için özel hazırladığımız kahvaltı tabakları."
        },
        {
          id: 7,
          thumbnail: "https://images.unsplash.com/photo-1498804103079-a6351b050096?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1498804103079-a6351b050096?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "coffee",
          caption: "Kahve Çekirdeklerimiz",
          description: "Dünya'nın dört bir yanından özenle seçtiğimiz kahve çekirdekleri."
        },
        {
          id: 8,
          thumbnail: "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "events",
          caption: "Kafe Etkinliklerimiz",
          description: "Düzenli olarak gerçekleştirdiğimiz müzik ve sanat etkinlikleri."
        },
        {
          id: 9,
          thumbnail: "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "pastries",
          caption: "El Yapımı Kurabiyelerimiz",
          description: "Geleneksel tariflere sadık kalarak hazırladığımız ev yapımı kurabiyeler."
        },
        {
          id: 10,
          thumbnail: "https://images.unsplash.com/photo-1559305616-3f99cd43e353?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1559305616-3f99cd43e353?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "team",
          caption: "Profesyonel Ekibimiz",
          description: "Her biri alanında uzman barista ve şeflerden oluşan ekibimiz."
        },
        {
          id: 11,
          thumbnail: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "coffee",
          caption: "Espresso Çeşitlerimiz",
          description: "İtalyan tarzı hazırladığımız espresso ve türevleri."
        },
        {
          id: 12,
          thumbnail: "https://images.unsplash.com/photo-1445116572660-236099ec97a0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1445116572660-236099ec97a0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "cafe",
          caption: "Dış Mekan Oturma Alanımız",
          description: "Güzel havalarda keyif yapabileceğiniz bahçe bölümümüz."
        },
        {
          id: 13,
          thumbnail: "https://images.unsplash.com/photo-1514066558159-fc8c737ef259?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1514066558159-fc8c737ef259?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "pastries",
          caption: "Kruvasan Çeşitlerimiz",
          description: "Fransız usulü hazırladığımız taze ve katmanlı kruvasanlar."
        },
        {
          id: 14,
          thumbnail: "https://images.unsplash.com/photo-1503481766315-7a586b20f66d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1503481766315-7a586b20f66d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "events",
          caption: "Müzik Gecelerimiz",
          description: "Canlı müzik performanslarıyla renklendirdiğimiz özel geceler."
        },
        {
          id: 15,
          thumbnail: "https://images.unsplash.com/photo-1507914997799-d84c6293f786?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80",
          full: "https://images.unsplash.com/photo-1507914997799-d84c6293f786?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=90",
          category: "team",
          caption: "Kahve Eğitimlerimiz",
          description: "Kahve tutkunları için düzenlediğimiz barista eğitim programları."
        },
      ];
      
      setImages(galleryImages);
      setFilteredImages(galleryImages);
      setLoading(false);
    }, 1000); // Simulated loading delay
  }, []);

  // Handle filtering of images by category
  const handleFilterChange = useCallback((category) => {
    setActiveFilter(category);
    if (category === 'all') {
      setFilteredImages(images);
    } else {
      setFilteredImages(images.filter(img => img.category === category));
    }
  }, [images]);

  // Open image modal/lightbox
  const openModal = useCallback((image) => {
    setSelectedImage(image);
    setModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  }, []);

  // Close image modal/lightbox
  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = ''; // Enable scrolling again
  }, []);

  // Add keyboard event listener for ESC key to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && modalOpen) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [modalOpen, closeModal]);

  // Extract unique categories for filter buttons
  const categories = [...new Set(['all', ...images.map(img => img.category)])];

  // Get layout class based on selected layout style
  const getLayoutClass = useCallback(() => {
    switch(layout) {
      case 'grid':
        return 'gallery-grid';
      case 'fullwidth':
        return 'gallery-fullwidth';
      default:
        return 'gallery-masonry';
    }
  }, [layout]);

  // Randomize layout sizes for masonry effect
  const getRandomLayout = useCallback(() => {
    const layouts = ['small', 'medium', 'large', 'tall', 'wide'];
    return layouts[Math.floor(Math.random() * layouts.length)];
  }, []);

  return (
    <div className="bg-light min-h-screen pt-16 sm:pt-20 md:pt-24 pb-12 px-3 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-xl sm:rounded-2xl mb-8 sm:mb-12 bg-dark">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-black/40 z-10"></div>
            <div className="gallery-hero-grid">
              {Array(5).fill().map((_, i) => (
                <div key={i} className="gallery-hero-item animate-fade-in" style={{ animationDelay: `${i * 0.2}s` }}>
                  <img 
                    src={i < images.length ? images[i].thumbnail : ''} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="relative z-20 text-center p-8 sm:p-12 md:p-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-playfair font-bold text-white mb-4 animate-slide-up">
              Galeri
            </h1>
            <p className="text-white/80 max-w-2xl mx-auto text-base sm:text-lg animate-slide-up animation-delay-300">
              Kafemizden, özel kahvelerimizden ve etkinliklerimizden görüntüler. Keyifli anılarımızı ve lezzetlerimizi keşfedin.
            </p>
          </div>
        </div>

        {/* Filters and Layout Switcher */}
        <div className="mb-8 sm:mb-10 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center animate-fade-in">
          {/* Category Filters */}
          <div className="overflow-x-auto hide-scrollbar pb-2 w-full sm:w-auto">
            <div className="flex space-x-2 min-w-max">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleFilterChange(category)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    activeFilter === category
                      ? 'bg-secondary text-white shadow-md font-medium'
                      : 'bg-white text-gray-700 hover:bg-gray-100 hover:shadow'
                  }`}
                >
                  {category === 'all' ? 'Tümü' : 
                    category === 'cafe' ? 'Kafemiz' :
                    category === 'coffee' ? 'Kahveler' :
                    category === 'pastries' ? 'Tatlılar' :
                    category === 'breakfast' ? 'Kahvaltı' :
                    category === 'events' ? 'Etkinlikler' :
                    category === 'team' ? 'Ekibimiz' : category
                  }
                </button>
              ))}
            </div>
          </div>

          {/* Layout Switcher */}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => setLayout('masonry')}
              className={`p-2.5 rounded-md transition-all ${layout === 'masonry' ? 'bg-secondary text-white' : 'bg-white text-gray-700'}`}
              aria-label="Mozaik Görünüm"
              title="Mozaik Görünüm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16M8 4v7M16 13v7M4 12h16" />
              </svg>
            </button>
            <button
              onClick={() => setLayout('grid')}
              className={`p-2.5 rounded-md transition-all ${layout === 'grid' ? 'bg-secondary text-white' : 'bg-white text-gray-700'}`}
              aria-label="Izgara Görünüm"
              title="Izgara Görünüm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16M4 12h16" />
              </svg>
            </button>
            <button
              onClick={() => setLayout('fullwidth')}
              className={`p-2.5 rounded-md transition-all ${layout === 'fullwidth' ? 'bg-secondary text-white' : 'bg-white text-gray-700'}`}
              aria-label="Tam Genişlik Görünüm"
              title="Tam Genişlik Görünüm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 12h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Gallery */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-coffee">☕</div>
            <p className="ml-3 text-lg text-primary">Galeri yükleniyor...</p>
          </div>
        ) : (
          <div className={`${getLayoutClass()} animate-fade-in`}>
            {filteredImages.map((image) => (
              <GalleryItem
                key={image.id}
                image={image}
                onClick={openModal}
                layout={layout === 'masonry' ? getRandomLayout() : ''}
              />
            ))}
          </div>
        )}

        {/* No results message */}
        {!loading && filteredImages.length === 0 && (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">Bu kategoride henüz fotoğraf bulunmuyor.</p>
            <button 
              onClick={() => handleFilterChange('all')}
              className="mt-4 px-6 py-2 bg-secondary text-white rounded-lg hover:bg-primary transition-all"
            >
              Tüm fotoğrafları göster
            </button>
          </div>
        )}

        {/* Image modal/lightbox */}
        <ImageModal 
          isOpen={modalOpen} 
          imageData={selectedImage} 
          onClose={closeModal}
        />

        {/* CTA Section */}
        <div className="mt-16 animate-on-scroll opacity-0">
          <div className="bg-white rounded-xl shadow-md p-8 sm:p-10 md:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 font-playfair text-dark">Anılarınızı Bizimle Paylaşın</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Kafemizde geçirdiğiniz keyifli anları sosyal medyada #KafemizdeKeyif etiketiyle paylaşarak galerimize katkıda bulunabilirsiniz.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 hover:from-purple-600 hover:to-pink-600 transition-all">
                <i className="fab fa-instagram"></i>
                Instagram'da Bizi Takip Edin
              </a>
              <a href="/contact" className="bg-secondary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary transition-colors">
                İletişime Geçin
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GaleriPage;
